import React, { useState, useRef } from 'react';
import './FileUpload.css';

const FileUpload = ({ onFilesSelected, onClose, maxFiles = 5, maxSize = 10 * 1024 * 1024 }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const allowedTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain', 'application/zip', 'application/x-rar-compressed'
  ];

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const validateFiles = (files) => {
    const errors = [];
    const validFiles = [];

    files.forEach(file => {
      // Check file size
      if (file.size > maxSize) {
        errors.push(`${file.name} is too large. Maximum size is ${formatFileSize(maxSize)}`);
        return;
      }

      // Check file type
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name} is not a supported file type`);
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errors };
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;

    const { validFiles, errors } = validateFiles(files);

    if (errors.length > 0) {
      alert('File validation errors:\n' + errors.join('\n'));
      return;
    }

    if (validFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // For chat, we don't pre-upload files - we just pass them to the parent
    // The files will be uploaded when the message is sent
    onFilesSelected(validFiles);
    
    // Close the file upload component
    if (onClose) {
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="file-upload">
      <div
        className={`upload-area ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={allowedTypes.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
                 <div className="upload-content">
           <div className="upload-icon">
             📎
           </div>
           <p className="upload-text">
             {isDragging ? 'Drop files here' : 'Click to select files or drag and drop'}
           </p>
           <p className="upload-hint">
             Max {maxFiles} files, {formatFileSize(maxSize)} each
           </p>
           <p className="upload-formats">
             Supported: Images, PDFs, Documents, Archives
           </p>
         </div>
         
         {onClose && (
           <button 
             className="close-upload-btn"
             onClick={(e) => {
               e.stopPropagation();
               onClose();
             }}
             title="Close"
           >
             ✕
           </button>
         )}
      </div>


    </div>
  );
};

export default FileUpload;
